import { useState } from 'react';

import cs from 'classnames';
import Tippy from '@tippyjs/react';

import { ia, iaRa } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import ProgressBar from 'components/shared/ProgressBar/ProgressBar';

import tiffFileIcon from 'assets/Icons/file/tiff.svg';

import {
  DOCUMENT_TYPES,
  ICON_TYPES,
  STATUSES,
  TRY_AGAIN_LABEL
} from '../FileUploader/lib/constants';

import { withCategory } from './lib/helpers';
import PreviewFile from './components/PreviewFile';
import CategoryPicker from './components/CategoryPicker';

/**
 * @param {import('./lib/propTypes').FileProps} props
 */

const File = ({
  file,
  onDelete,
  onRetry,
  onEdit,
  status = 'uploading',
  hidePreview = false,
  setPendingFiles,
  categories = [],
  isPending = false,
  isSelectable = false,
  isSelected = false,
  onSelect
}) => {
  const [preview, setPreview] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(false);

  if (!file) return null;

  const fileType = file?.type;
  const isImage = fileType?.startsWith('image/');
  const isTiff = fileType?.includes('tiff') || fileType?.includes('tif');

  const isFailed = status === 'failed';
  const isUploading = status === 'uploading';
  const icon = ICON_TYPES[status];

  const togglePreview = () => setPreview((prev) => !prev);

  const handleCategoryChange = (fileId, category) =>
    setPendingFiles((prev) =>
      iaRa(prev).map((file) => (file.fileId === fileId ? withCategory(file, category) : file))
    );

  const statusIsNotIdle = status !== 'idle';
  const isActive = !isUploading && !isFailed;
  return (
    <>
      <div
        className={cs(
          'flex h-fit w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-200 bg-white p-4',
          isFailed ? 'border-danger-500' : 'border-neutral-300'
        )}>
        <div className="flex h-fit w-full flex-row items-start gap-3">
          {isSelectable && isActive && (
            <Checkbox isChecked={isSelected} onChange={() => onSelect?.(file)} />
          )}
          {isTiff ? (
            <img src={tiffFileIcon} alt="" className="h-6 w-6 shrink-0" aria-hidden="true" />
          ) : (
            <Icon icon={DOCUMENT_TYPES[fileType] || 'file-default'} />
          )}
          <div className="flex w-full flex-col items-start">
            <span
              className={cs(
                'flex max-w-full cursor-default items-center overflow-hidden break-all text-sm font-medium leading-[22px] text-neutral-900',
                !isFailed && 'cursor-pointer hover:opacity-70'
              )}
              title={file.name}
              onClick={togglePreview}>
              {file.name}
            </span>
            <div className="flex cursor-default items-center gap-1 text-xs font-normal leading-5 text-neutral-700 ">
              {statusIsNotIdle ? (
                <>
                  <span className="flex items-center ">
                    {`${Math.round(file.uploadedSize / 1000)} KB of ${Math.round(file.size / 1000)} KB`}
                  </span>
                  <div className="h-0.5 w-0.5 rounded-full bg-neutral-500" />
                  <div className="flex items-center gap-1">
                    <Icon
                      icon={icon}
                      className={cs(isUploading ? 'animate-spin' : '')}
                      {...(isFailed && { color: 'danger', shade: 500 })}
                    />
                    <span>{STATUSES[status]}</span>
                  </div>
                </>
              ) : (
                <span className="flex items-center ">{`${Math.round(file.size / 1000)} KB`}</span>
              )}
            </div>
            {isFailed && (
              <span
                onClick={() => onRetry(file.fileId)}
                className={cs(
                  'text-red-500 flex items-center text-sm leading-[22px] underline',
                  'cursor-pointer hover:opacity-70 '
                )}>
                {TRY_AGAIN_LABEL}
              </span>
            )}
          </div>

          {isActive && ia(categories) && (
            <CategoryPicker
              file={file}
              categories={categories}
              onCategoryChange={handleCategoryChange}
            />
          )}

          <div className="flex gap-1">
            {isActive && !hidePreview && (
              <Tippy content="Preview file" className="tippy-dark">
                <div
                  className="flex cursor-pointer items-center justify-center rounded-md p-1 hover:bg-neutral-75"
                  onClick={togglePreview}>
                  <Icon
                    size={16}
                    icon="document-preview-mono"
                    color="neutral"
                    shade={700}
                    className="cursor-pointer"
                  />
                </div>
              </Tippy>
            )}
            {isActive && isImage && !isTiff && onEdit && (
              <Tippy content="Edit photo" className="tippy-dark">
                <div
                  className="flex cursor-pointer items-center justify-center rounded-md p-1 hover:bg-neutral-75"
                  onClick={() => onEdit(file)}>
                  <Icon
                    size={16}
                    icon="edit-3-mono"
                    color="neutral"
                    shade={700}
                    className="cursor-pointer"
                  />
                </div>
              </Tippy>
            )}
            {isActive && onDelete && (
              <Tippy content={isPending ? 'Remove file' : 'Delete file'} className="tippy-dark">
                <div
                  className="flex cursor-pointer items-center justify-center rounded-md p-1 hover:bg-neutral-75"
                  onClick={() =>
                    isPending ? onDelete(file.handle, file.fileId) : setDeleteWarning(true)
                  }>
                  <Icon
                    size={16}
                    icon={isPending ? 'close-linear' : 'trash-mono'}
                    className="cursor-pointer"
                    color={isFailed ? 'danger' : 'neutral'}
                    shade={isFailed ? 500 : 700}
                  />
                </div>
              </Tippy>
            )}
          </div>
        </div>
        {isUploading && <ProgressBar progress={file.progress} />}
      </div>
      <PreviewFile
        isOpen={preview}
        type={fileType}
        handle={file.handle}
        name={file.name}
        onClose={togglePreview}
        fileBlob={file.originalFile}
      />
      <Confirm
        isOpen={deleteWarning}
        handleClose={() => setDeleteWarning(false)}
        handleContinue={() => {
          onDelete(file.handle, file.fileId);
          setDeleteWarning(false);
        }}
        title="Delete file"
        message="Are you sure you want to delete this file?"
        variant="danger"
        icon="file-unknown"
        footerActions={{
          primary: {
            onClick: () => {
              onDelete(file.handle, file.fileId);
              setDeleteWarning(false);
            },
            color: 'danger'
          }
        }}
      />
    </>
  );
};
export default File;
